/**
 * Sentinel Response™ - Main App
 * Routes users to the correct portal based on their role.
 */
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layouts
import SentinelLayout from './components/SentinelLayout';
import ClientLayout from './components/ClientLayout';

// Pages
import LoginPage from './pages/LoginPage';
import CallbackPage from './pages/CallbackPage';
import DashboardPage from './pages/DashboardPage';
import TicketListPage from './pages/TicketListPage';
import TicketDetailPage from './pages/TicketDetailPage';
import NewTicketPage from './pages/NewTicketPage';
import AssetsPage from './pages/AssetsPage';
import MonitoringPage from './pages/MonitoringPage';
import LoadingScreen from './components/LoadingScreen';

function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, loading, role } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }
  return children || <Outlet />;
}

function RoleRouter() {
  const { isSentinelUser, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return isSentinelUser
    ? <Navigate to="/sentinel/dashboard" replace />
    : <Navigate to="/portal/dashboard" replace />;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />

        {/* Role-based redirect */}
        <Route path="/" element={<ProtectedRoute><RoleRouter /></ProtectedRoute>} />

        {/* Sentinel Ops Portal */}
        <Route
          path="/sentinel"
          element={
            <ProtectedRoute allowedRoles={['sentinel_admin', 'sentinel_tech']}>
              <SentinelLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="tickets" element={<TicketListPage />} />
          <Route path="tickets/new" element={<NewTicketPage />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="monitoring" element={<MonitoringPage />} />
        </Route>

        {/* Client Portal */}
        <Route
          path="/portal"
          element={
            <ProtectedRoute allowedRoles={['client_admin', 'client_user']}>
              <ClientLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="tickets" element={<TicketListPage />} />
          <Route path="tickets/new" element={<NewTicketPage />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="monitoring" element={<MonitoringPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
